import React, { useState } from 'react';
import Button from '../ui/Button';
import { usePortfolio } from '../../contexts/PortfolioContext';
import { Project } from '../../types/entityTypes';

const Projects: React.FC = () => {
  const { data, loading, error } = usePortfolio();

  const [filter, setFilter] = useState<'all' | 'featured'>('all');

  if (loading) return <div></div>;
  if (error) return <div>Error: {error}</div>;

  const projects: Project[] = data?.projects;
  const filteredProjects = filter === 'all' ? projects : projects.filter(p => p.featured);
  const projectStory: Record<string, { problem: string; intent: string; direction: string }> = {
    LetsBoostUp: {
      problem: 'Interview preparation is fragmented across endless resources, with little clarity about what to learn next or whether progress is real.',
      intent: 'Create a guided preparation environment that brings coding, system design, structured learning and intelligent assistance into one coherent journey.',
      direction: 'A more adaptive learning companion that understands gaps, momentum and the kind of role each person is working toward.'
    },
    Acturum: {
      problem: 'We save valuable articles, videos and ideas—but most of that knowledge quietly becomes an archive instead of changing what we do.',
      intent: 'Turn passive content into concrete, prioritised actions with AI-supported capture, understanding, memory and daily execution.',
      direction: 'A personal execution layer that connects what you learn with what you actually finish.'
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString || dateString.toLowerCase() === 'present') return 'Present';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // fallback to raw string if unparseable
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
  };

  return (
    <section id="projects" className="projects-section py-24 transition-colors duration-300">
      <div className="section-transition" aria-hidden="true">
        <span>From capability to product</span>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="projects-heading mb-14">
          <div>
          <span className="projects-eyebrow">What I’m building / 02</span>
          <h2 className="text-4xl md:text-6xl font-bold text-white mt-3 mb-4 transition-colors duration-300">
            Products shaped from problems I care about.
          </h2>
          </div>
          <p className="text-lg text-gray-400 max-w-xl leading-relaxed transition-colors duration-300">
            These are not portfolio exercises. They are evolving attempts to solve problems I have noticed, understood and chosen to pursue.
          </p>

          {/* Filter Buttons */}
          <div className="projects-filters flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`project-filter ${filter === 'all' ? 'is-active' : ''}`}
            >
              All Projects ({projects.length})
            </button>
            <button
              onClick={() => setFilter('featured')}
              className={`project-filter ${filter === 'featured' ? 'is-active' : ''}`}
            >
              Featured ({projects.filter(p => p.featured).length})
            </button>
          </div>
        </div>

        {/* Projects List — each project full width */}
        <div className="space-y-8">
          {filteredProjects.map((project) => (
            <article key={project.id} className={`project-card ${project.title === 'LetsBoostUp' ? 'is-letsboostup' : ''} ${project.title === 'Acturum' ? 'is-acturum' : ''} rounded-3xl overflow-hidden transition-all duration-300`}>
              {/* Project Image or Gradient Placeholder */}
              {project.imageUrl ? (
                <div
                  className={`project-visual ${project.title === 'LetsBoostUp' || project.title === 'Acturum' ? 'project-visual-logo' : ''} ${project.title === 'Acturum' ? 'project-visual-acturum' : ''} w-full h-48 md:h-64 relative bg-contain bg-center bg-no-repeat bg-gray-100 dark:bg-gray-700`}
                  style={{ backgroundImage: `url(${process.env.PUBLIC_URL}${project.imageUrl})` }}
                >
                  <div className="project-image-overlay absolute inset-0" />
                  {/* Badges */}
                  <div className={`project-status absolute top-4 right-4 ${project.completed ? 'is-live' : ''}`}>
                    <span /> {project.completed ? 'Live product' : 'In progress'}
                  </div>
                  {project.featured && (
                    <div className="project-featured-label absolute top-4 left-4">Featured build</div>
                  )}
                </div>
              ) : (
                <div className="w-full h-32 md:h-40 relative bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                  <span className="text-6xl opacity-30">🚀</span>
                  <div className="absolute top-4 right-4">
                    <span>{project.completed ? 'Completed' : 'In Progress'}</span>
                  </div>
                  {project.featured && (
                    <div className="absolute top-4 left-4">
                      <span>Featured</span>
                    </div>
                  )}
                </div>
              )}

              {/* Project Content */}
              <div className="project-content p-6 md:p-8">
                <div>
                  <span className="project-sequence">PROJECT / {String(filteredProjects.indexOf(project) + 1).padStart(2, '0')}</span>
                  <h3 className="text-3xl md:text-4xl font-bold text-white mt-3 mb-2">
                    {project.title}
                  </h3>
                  <p className="project-date text-sm mb-4">
                    {formatDate(project.startDate)} - {project.endDate ? formatDate(project.endDate) : 'Present'}
                  </p>
                  <p className="project-description leading-relaxed">
                    {project.longDescription || project.description}
                  </p>
                  {projectStory[project.title] && (
                    <div className="project-story-grid">
                      <div><span>The problem</span><p>{projectStory[project.title].problem}</p></div>
                      <div><span>The intention</span><p>{projectStory[project.title].intent}</p></div>
                      <div><span>Where it can go</span><p>{projectStory[project.title].direction}</p></div>
                    </div>
                  )}
                </div>

                <div className="project-side-column">
                  {/* Technologies */}
                  <div>
                    <h4 className="project-stack-label mb-3">Built with</h4>
                    <div className="flex flex-wrap gap-2">
                      {project.technologies.map((tech) => (
                        <span className="project-tech" key={tech}>{tech}</span>
                      ))}
                    </div>
                  </div>

                  {/* Project Links */}
                  <div className="project-actions flex flex-wrap gap-3">
                    {project.liveUrl && (
                      <Button variant="primary" size="sm" href={project.liveUrl} target="_blank">
                        Visit live product ↗
                      </Button>
                    )}
                    {project.githubUrl && (
                      <Button variant="outline" size="sm" href={project.githubUrl} target="_blank">
                        View source ↗
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
